/**
 * The lists this agency keeps, all of them, on one screen.
 *
 * ## One screen because there is one mechanism
 *
 * How a payout was paid and where a client stands are the same sort of thing: a list of labels that
 * belongs to the agency, offered in a picker, added to where the choice is made. They are one table
 * and one route behind the scenes (ADR-0134), and if they were three screens here the agency would
 * learn that renaming an entry works differently depending on which list it is on. The sections
 * below are generated from `ORG_OPTION_KINDS`, so a list added to the product appears here with no
 * work at all — which is the entire return on having made it one mechanism.
 *
 * ## This is not where entries are normally added
 *
 * The picker on the form adds them, and that is deliberate: the moment somebody needs an entry the
 * list does not have is the moment they are in the middle of something else. What this screen is
 * for is the tidying that follows — a name typed in a hurry, a method the agency has stopped using,
 * a status somebody invented twice. Adding is offered here as well because a person who has come
 * here to tidy will sometimes want to put the replacement in first, and the refusal to archive the
 * last live entry tells them to do exactly that.
 *
 * ## Retired, never deleted
 *
 * A payout names how it was paid and a client names where they stand, and both records have to go
 * on saying so. Archiving takes an entry out of the pickers and leaves everything already pointing
 * at it untouched. The count beside a client status is there for the moment before that decision:
 * how many people am I about to leave with a status nobody offers.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ORG_OPTION_KINDS, ORG_OPTION_LABELS, OPTION_NAME_MAX, type OptionTone, type OrgOptionKind } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { AddOptionModal, OptionMark, useOrgOptions, type OrgOption } from "@/components/option-picker";
import { Alert, Button, Card, CardTitle, Checkbox, ErrorState, Field, Input, Modal, PageHeader, Select, Spinner, Table } from "@/components/ui";

/** What each list is for, in the agency's terms rather than the table's. */
const PURPOSE: Record<OrgOptionKind, string> = {
  payout_method: "Offered when a payout is marked paid, so the record says how the money actually left.",
  client_status: "Offered on a client, and drawn beside their name wherever they appear.",
};

export function ListsPage() {
  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Your lists"
        description={
          "The lists this agency keeps for itself. Each one is added to from the form where it is used; this is where " +
          "entries are renamed and retired."
        }
      />
      <div className="space-y-5">
        {ORG_OPTION_KINDS.map((kind) => (
          <OptionList key={kind} kind={kind} />
        ))}
      </div>
    </div>
  );
}

function OptionList({ kind }: { kind: OrgOptionKind }) {
  const { can } = useSession();
  const label = ORG_OPTION_LABELS[kind];
  const [showArchived, setShowArchived] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<OrgOption | null>(null);

  const { data, isLoading, error, refetch } = useOrgOptions(kind, { archived: showArchived });
  const rows = data?.options ?? [];
  const mayEdit = can("party.edit");

  /* Only the client statuses have anything to count. The endpoint answers for every kind so this
     needs no branch, and an empty answer is the same shape as a full one. */
  const usage = useQuery({
    queryKey: ["option-usage", kind],
    queryFn: () => api.get<{ usage: Array<{ optionId: string; count: number }> }>(`/options/${kind}/usage`),
  });
  const countFor = (id: string) => usage.data?.usage.find((entry) => entry.optionId === id)?.count ?? 0;

  const archive = useApiMutation({
    mutationFn: (input: { id: string }) => api.post(`/options/${kind}/${input.id}/archive`),
    invalidates: [["options", kind], ["options"], ["parties"], ["payouts"]],
  });

  return (
    /* Marked on the wrapper rather than read off the page: the two lists are one above the other
       and an agency is entirely at liberty to call a payout method "Active". A suite that searched
       the whole page for a name would pass on the wrong list. */
    <div data-option-list={kind}>
    <Card padded={false}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 p-4 sm:p-5">
        <CardTitle hint={PURPOSE[kind]}>{label.many}</CardTitle>
        <div className="flex flex-wrap items-center gap-4">
          <Checkbox
            label="Include retired"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />
          {mayEdit ? (
            <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
              Add
            </Button>
          ) : null}
        </div>
      </div>

      {archive.errorMessage ? (
        <div className="p-4 sm:p-5">
          <Alert tone="danger">{archive.errorMessage}</Alert>
        </div>
      ) : null}

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : (
        <Table
          head={
            <tr>
              <th>Entry</th>
              {kind === "client_status" ? <th className="num">Clients</th> : null}
              <th />
            </tr>
          }
        >
          {rows.map((row) => (
            <tr key={row.id} data-option-row={row.name}>
              <td>
                {kind === "client_status" ? (
                  <OptionMark name={row.name} tone={row.tone} />
                ) : (
                  <span className="font-medium text-ink-800">{row.name}</span>
                )}
                {row.archivedAt ? <span className="ml-2 text-xs text-ink-400">Retired</span> : null}
              </td>
              {kind === "client_status" ? (
                <td className="num tabular text-ink-500">{countFor(row.id) || "—"}</td>
              ) : null}
              <td className="num">
                {mayEdit ? (
                  <span className="inline-flex items-center gap-2">
                    <button type="button" className="text-sm text-ink-500 hover:text-ink-800" onClick={() => setEditing(row)}>
                      Rename
                    </button>
                    <button
                      type="button"
                      className="text-sm text-ink-500 hover:text-ink-800"
                      disabled={archive.isPending}
                      onClick={() => archive.mutate({ id: row.id })}
                    >
                      {row.archivedAt ? "Restore" : "Retire"}
                    </button>
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      )}

      <AddOptionModal kind={kind} open={adding} onClose={() => setAdding(false)} onCreated={() => setAdding(false)} />
      <RenameOptionModal kind={kind} option={editing} onClose={() => setEditing(null)} />
    </Card>
    </div>
  );
}

function RenameOptionModal({ kind, option, onClose }: { kind: OrgOptionKind; option: OrgOption | null; onClose: () => void }) {
  const [tone, setTone] = useState<OptionTone>("neutral");
  const [seeded, setSeeded] = useState<string | null>(null);

  if (option && seeded !== option.id) {
    setSeeded(option.id);
    setTone(option.tone);
  }

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/options/${kind}/${option?.id}`, input),
    invalidates: [["options", kind], ["options"], ["parties"], ["payouts"]],
    onSuccess: () => onClose(),
  });

  if (!option) return null;

  return (
    <Modal open onClose={onClose} title={`Rename ${option.name}`}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          const values = formValues(event.currentTarget);
          save.mutate({ name: values.renameOption, tone });
        }}
      >
        {save.errorMessage ? <Alert tone="danger">{save.errorMessage}</Alert> : null}

        {/* Renaming reaches every record that already names this entry, and that is right: the
            entry is one thing the agency calls by one name, and a payout marked "Transfer" before
            the correction was paid the same way as one marked "Bank transfer" after it. */}
        <Field
          label="Name"
          htmlFor="renameOption"
          required
          hint="Every record already carrying this entry reads the new name."
          error={save.fieldErrors.name}
        >
          <Input
            id="renameOption"
            name="renameOption"
            required
            maxLength={OPTION_NAME_MAX}
            defaultValue={option.name}
            placeholder={kind === "payout_method" ? "Bank transfer" : "Active"}
          />
        </Field>

        {kind === "client_status" ? (
          <Field label="How it is marked" htmlFor="renameTone">
            <Select id="renameTone" value={tone} onChange={(event) => setTone(event.target.value as OptionTone)}>
              <option value="neutral">No comment</option>
              <option value="active">A live relationship</option>
              <option value="attention">Somebody has to act</option>
              <option value="closed">Finished with</option>
            </Select>
          </Field>
        ) : null}

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
